/**
 * Everything that moves the camera but not the player: head bob, lateral sway,
 * strafe roll, the crouch height blend and the dip on landing.
 *
 * The bob is deliberately small. It exists so that walking reads as walking in
 * the corner of the eye, not so the player notices it. Two rules keep it from
 * becoming nausea: the amplitude scales with how fast the player is actually
 * moving (not with what key is held), and it eases out over a couple of tenths
 * of a second when they stop rather than freezing mid-arc.
 *
 * The phase is time-driven from `bobFrequency` while footsteps are distance-
 * driven from `stepInterval`; those two agree to within a few percent at every
 * speed, and `syncToStep` absorbs the drift at the bottom of the arc where a
 * phase correction has no visible effect.
 */

import { TUNING } from '../core/config';
import { clamp, clamp01, damp, TAU } from '../core/math';

/** How fast the bob envelope opens and closes, 1/seconds. */
const BOB_BLEND_RATE = 9;

/** How fast the strafe roll follows the input, 1/seconds. */
const ROLL_RATE = 7;

/** Landing dip spring, chosen critically damped: DAMPING = 2 * sqrt(STIFFNESS). */
const LAND_STIFFNESS = 170;
const LAND_DAMPING = 26;

/** Downward camera velocity per metre-per-second of impact, capped by MAX. */
const LAND_KICK = 0.055;
const LAND_KICK_MAX = 0.5;

/** Bob and sway are muted while crouched — the whole body is quieter. */
const CROUCH_BOB_SCALE = 0.55;

/** Ceiling on how much faster-than-walking speed can grow the bob. */
const MAX_SPEED_FACTOR = 1.7;

export interface HeadBob {
  /** Vertical offset to add to the eye height, metres. */
  readonly offsetY: number;
  /** Lateral sway in world space, metres. Add to the eye position, not to `pos`. */
  readonly offsetX: number;
  readonly offsetZ: number;
  /** Camera roll in radians. */
  readonly roll: number;
  /** Eye height above the feet from the crouch blend alone, metres. */
  readonly baseEye: number;
  /** True once the crouch blend has finished moving. */
  readonly settled: boolean;

  /**
   * @param dt       frame time, seconds
   * @param speed    horizontal speed in m/s
   * @param yaw      look yaw, used to place the sway in world space
   * @param strafe   -1..1 sideways input, drives the roll
   */
  update(
    dt: number,
    speed: number,
    yaw: number,
    strafe: number,
    sprinting: boolean,
    crouching: boolean,
    grounded: boolean
  ): void;

  /**
   * Turn the walk cycle off. The crouch blend, the landing dip and the strafe
   * roll are separate effects and keep running; this is only the oscillation,
   * which is the part the `headBob` setting is there to disable.
   */
  setEnabled(on: boolean): void;

  /** Pull the arc back to its low point. Called on each footstep. */
  syncToStep(): void;

  /** Kick the camera down proportionally to the impact speed in m/s. */
  land(impactSpeed: number): void;

  /** Drop all motion, e.g. on teleport. */
  reset(crouching: boolean): void;
}

class Bob implements HeadBob {
  offsetY = 0;
  offsetX = 0;
  offsetZ = 0;
  roll = 0;
  baseEye = TUNING.player.eyeHeight;
  settled = true;

  /** Radians through the current bob arc; 0 is the footfall, the low point. */
  private phase = 0;
  /** 0..1 envelope so the bob fades in and out instead of snapping. */
  private weight = 0;
  /** Flips every footfall so the sway leans left, right, left. */
  private swaySign = 1;
  /** Strafe roll before the sway is added, so the two do not feed back. */
  private rollBase = 0;
  private crouchT = 0;
  private landOffset = 0;
  private landVel = 0;
  private enabled = true;

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) {
      this.offsetX = 0;
      this.offsetZ = 0;
    }
  }

  update(
    dt: number,
    speed: number,
    yaw: number,
    strafe: number,
    sprinting: boolean,
    crouching: boolean,
    grounded: boolean
  ): void {
    const P = TUNING.player;

    const crouchRate = P.crouchTime > 0 ? dt / P.crouchTime : 1;
    const target = crouching ? 1 : 0;
    if (this.crouchT < target) this.crouchT = Math.min(target, this.crouchT + crouchRate);
    else if (this.crouchT > target) this.crouchT = Math.max(target, this.crouchT - crouchRate);
    this.settled = this.crouchT === target;
    this.baseEye = P.eyeHeight + (P.crouchEyeHeight - P.eyeHeight) * this.crouchT;

    const speedFactor = clamp(speed / P.walkSpeed, 0, MAX_SPEED_FACTOR);
    // Airborne there are no footfalls, so the arc holds where it is and fades.
    const moving = grounded && speed > 0.15;
    this.weight = damp(this.weight, moving ? 1 : 0, BOB_BLEND_RATE, dt);
    if (moving) this.phase = (this.phase + P.bobFrequency * speedFactor * dt) % TAU;

    const amp = this.enabled
      ? P.bobAmplitude * (sprinting ? P.sprintBobScale : 1) * (crouching ? CROUCH_BOB_SCALE : 1) * speedFactor * this.weight
      : 0;

    // Landing dip: a critically damped spring so it returns without ringing.
    this.landVel += (-LAND_STIFFNESS * this.landOffset - LAND_DAMPING * this.landVel) * dt;
    this.landOffset += this.landVel * dt;

    this.offsetY = -amp * 0.5 * Math.cos(this.phase) + this.landOffset;

    // Half the bob rate, with the sign flipped each footfall, so the weight
    // shifts onto alternate feet instead of always leaning the same way.
    const sway = this.enabled
      ? P.swayAmplitude *
        (sprinting ? 1.25 : 1) *
        (crouching ? CROUCH_BOB_SCALE : 1) *
        this.weight *
        this.swaySign *
        Math.sin(this.phase * 0.5)
      : 0;
    // Right-hand vector for this yaw, matching yaw = 0 looking down -Z.
    this.offsetX = Math.cos(yaw) * sway;
    this.offsetZ = Math.sin(yaw) * sway;

    const rollTarget = -P.strafeRoll * clamp(strafe, -1, 1) * clamp01(speed / P.walkSpeed);
    this.rollBase = damp(this.rollBase, rollTarget, ROLL_RATE, dt);
    this.roll = this.rollBase + sway * 0.35;
  }

  syncToStep(): void {
    this.phase = 0;
    this.swaySign = -this.swaySign;
  }

  land(impactSpeed: number): void {
    this.landVel -= Math.min(impactSpeed * LAND_KICK, LAND_KICK_MAX);
  }

  reset(crouching: boolean): void {
    this.phase = 0;
    this.weight = 0;
    this.swaySign = 1;
    this.rollBase = 0;
    this.landOffset = 0;
    this.landVel = 0;
    this.offsetX = 0;
    this.offsetY = 0;
    this.offsetZ = 0;
    this.roll = 0;
    this.crouchT = crouching ? 1 : 0;
    this.settled = true;
    this.baseEye = crouching ? TUNING.player.crouchEyeHeight : TUNING.player.eyeHeight;
  }
}

export function makeHeadBob(): HeadBob {
  return new Bob();
}
